/**
 * Quiz Klaviyo Integration
 *
 * Three flows:
 *
 * 1. QUIZ COMPLETION (quiz:filtered)
 *    Subscribes to klaviyoConfig.quizListId with full pet profile + recommended
 *    products, then closes the quiz modal.
 *
 * 2. QUIZ ABANDONED (close button click or page exit)
 *    Subscribes to klaviyoConfig.abandonedListId with mid-quiz state.
 *    Only fires if the user is already a Klaviyo subscriber (has the __kla_id
 *    cookie from the site's embedded signup popup) — we read their email from
 *    that cookie.
 *
 * 3. MEAL PLAN SELECTED (optimeal:meal-plan-selected, dispatched by
 *    quiz-results.js on Save in the product edit modal)
 *    Refreshes klaviyoConfig.quizListId with the customer's actual chosen
 *    products/quantities — the profile from flow 1 only ever had the initial
 *    algorithmic recommendation. Same __kla_id cookie identification as flow 2,
 *    since the results page is a separate page load with no email in memory.
 *
 * Completion profile properties (example — 1 cat):
 * {
 *   pet_count: 1,
 *   pet_1_name: "Whiskers",
 *   pet_1_activity: "Low",
 *   pet_1_weight_lb: 10,
 *   pet_1_pouches: 48,
 *   pet_1_recommendations: [
 *     { title: "...", url: "/products/...", price: "...", image_url: "..." },
 *     ...up to 3
 *   ],
 *   // + one pet_1_<handle> property per answered question, generic — see
 *   // pet_1_food_usage, pet_1_health_conditions, pet_1_health_outcome below
 *   pet_1_love_proteins: "chicken, salmon",
 *   pet_1_food_usage: "Daily food",
 *   pet_1_health_conditions: "None",
 *   pet_1_health_outcome: "Weight management"
 * }
 *
 * Abandoned profile properties (example):
 * {
 *   quiz_abandoned: true,
 *   quiz_last_step: 2,
 *   quiz_pet_count: 1,
 *   quiz_pet_1_name: "Whiskers",
 *   quiz_pet_1_food_usage: "Daily food"
 * }
 *
 * Any quiz question answered by a pet — inline/frame step or grouped form
 * step, single-select or multiselect — shows up automatically as
 * pet_N_<handle> (handle = the question's Shopify metaobject handle, e.g.
 * "food-usage", "health-conditions", "love-proteins"). Adding a new quiz
 * question in Shopify Admin requires no change here.
 */

// ─── Helpers ──────────────────────────────────────────────────────────────────

/**
 * Read the email of an existing Klaviyo subscriber from the __kla_id cookie.
 * Klaviyo sets this cookie when a user subscribes via an embedded signup form.
 * Returns null if not found or not parseable.
 */
function getKlaviyoSubscriberEmail() {
  const match = document.cookie.split('; ').find(c => c.startsWith('__kla_id='));
  if (!match) return null;
  try {
    const raw = match.split('=')[1];
    const decoded = JSON.parse(atob(decodeURIComponent(raw)));
    return decoded.$email || null;
  } catch {
    return null;
  }
}

/**
 * Strip HTML from quiz answer text — answer text is rich text sourced from
 * Shopify metafields, so it commonly arrives wrapped in tags like <p>.
 */
function stripHtml(str) {
  const el = document.createElement('div');
  el.innerHTML = str || '';
  return (el.textContent || el.innerText || '').trim();
}

/**
 * Flatten one answersByHandle entry into a plain string, for either shape:
 * single-select ({ text, tag }) or multiselect ({ multiselect: true, selections }).
 */
function flattenAnswer(answer) {
  if (!answer) return '';
  if (answer.multiselect) {
    return (answer.selections || [])
      .map((sel) => stripHtml(sel.text) || sel.tag || '')
      .filter(Boolean)
      .join(', ');
  }
  return stripHtml(answer.text) || answer.tag || '';
}

/**
 * Build flat Klaviyo profile properties from completed quiz state.
 *
 * Named fields (name, activity, pouches, weight_lb) are kept explicit since
 * other Klaviyo flows/templates already reference them by those exact names.
 * Every other answered question — regardless of handle, and without needing
 * a code change when a new quiz question is added — is forwarded generically
 * from pet.answersByHandle as pet_N_<handle>.
 *
 * No generic "tags" property: every question that used to feed it (protein
 * preference, life stage, health outcome, etc.) already has its own
 * pet_N_<handle> property above and those are more precise — the old
 * pet_N_tags merged loved and hated protein names into one string with no
 * sentiment marker. Removed 2026-07-22; use pet_N_love_proteins /
 * pet_N_hate_proteins for protein sentiment.
 */
function buildKlaviyoProperties(petCount) {
  const resultPets = window.OptimealResults?.pets || [];
  const statePets = window.OptimealQuizState?.pets || [];

  const properties = { pet_count: petCount };

  resultPets.forEach((pet, i) => {
    const prefix = `pet_${i + 1}_`;
    const statePet = statePets[i] || {};

    if (pet.name) properties[`${prefix}name`] = pet.name;
    if (pet.activity) properties[`${prefix}activity`] = pet.activity;
    if (pet.pouches != null) properties[`${prefix}pouches`] = pet.pouches;

    const weight = statePet.formAnswers?.weight;
    if (weight != null) properties[`${prefix}weight_lb`] = weight;

    Object.entries(pet.answersByHandle || {}).forEach(([handle, answer]) => {
      const value = flattenAnswer(answer);
      if (value) properties[`${prefix}${handle.replace(/-/g, '_')}`] = value;
    });
  });

  return properties;
}

/**
 * Build a shareable results URL with encoded pet state as a ?q= param.
 * The results page decodes this and re-fetches products from Shopify, so the
 * link works cross-device (e.g. email → different browser).
 *
 * Encoded state per pet: name, pouches, activity, tags, answerTexts,
 * loved proteins, hated proteins (used for product sorting).
 */
function buildResultsUrl() {
  const pets = (window.OptimealResults?.pets || []).map((pet) => {
    const loved = (pet.answersByHandle?.['love-proteins']?.selections ?? []).map((s) => ({ tag: s.tag }));
    const hated = (pet.answersByHandle?.['hate-proteins']?.selections ?? []).map((s) => ({ tag: s.tag }));
    console.log(`[klaviyo] buildResultsUrl pet "${pet.name}":`, { loved, hated });
    return {
      name: pet.name,
      pouches: pet.pouches,
      activity: pet.activity,
      tags: pet.tags,
      answerTexts: pet.answerTexts,
      loved,
      hated,
    };
  });

  try {
    const encoded = btoa(unescape(encodeURIComponent(JSON.stringify({ pets }))));
    return `${window.location.origin}/pages/quiz-results?q=${encodeURIComponent(encoded)}`;
  } catch {
    return `${window.location.origin}/pages/quiz-results`;
  }
}

/**
 * Build per-pet recommended product arrays for the completed quiz.
 * Top 3 products per pet, stored as pet_N_recommendations: [{title, url, price, image_url}]
 * Use in Klaviyo email templates: {% for p in person|lookup:'pet_1_recommendations' %}
 */
function buildRecommendedProductsProperties() {
  const resultPets = window.OptimealResults?.pets || [];
  const properties = {};
  const MAX_RECS = 3;

  resultPets.forEach((pet, i) => {
    const top = (pet.products || []).slice(0, MAX_RECS);
    properties[`pet_${i + 1}_recommendations`] = top.map(product => {
      const rec = {
        title: product.title,
        url: `/products/${product.handle}`,
      };
      if (product.price?.amount) {
        rec.price = `${product.price.amount} ${product.price.currencyCode || ''}`.trim();
      }
      if (product.image?.url) {
        rec.image_url = product.image.url;
      }
      return rec;
    });
  });

  return properties;
}

/**
 * Build profile properties for an abandoned quiz.
 * Captures how far the user got: step number, pet count, names, and every
 * answered question forwarded generically as quiz_pet_N_<handle>.
 */
function buildAbandonedProperties() {
  const state = window.OptimealQuizState;
  const properties = { quiz_abandoned: true };

  const currentStep = window.quiz?.currentStep;
  if (currentStep != null) properties.quiz_last_step = currentStep;

  if (state?.familyQuantity) properties.quiz_pet_count = state.familyQuantity;

  (state?.pets || []).forEach((pet, i) => {
    const prefix = `quiz_pet_${i + 1}_`;
    if (pet.name) properties[`${prefix}name`] = pet.name;

    Object.entries(pet.answersByHandle || {}).forEach(([handle, answer]) => {
      const value = flattenAnswer(answer);
      if (value) properties[`${prefix}${handle.replace(/-/g, '_')}`] = value;
    });
  });

  return properties;
}

/**
 * POST to Klaviyo client subscriptions API.
 * Set keepalive: true when calling from pagehide so the request survives page unload.
 */
async function subscribeToList(email, listId, properties, { keepalive = false } = {}) {
  const config = window.klaviyoConfig;
  const res = await fetch(`https://a.klaviyo.com/client/subscriptions/?company_id=${config.companyId}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'revision': '2024-10-15',
    },
    keepalive,
    body: JSON.stringify({
      data: {
        type: 'subscription',
        attributes: {
          profile: {
            data: {
              type: 'profile',
              attributes: { email, properties },
            },
          },
        },
        relationships: {
          list: {
            data: { type: 'list', id: listId },
          },
        },
      },
    }),
  });
  return res;
}

// ─── Flow 1: Quiz completion ───────────────────────────────────────────────────

let quizCompleted = false;

document.addEventListener('quiz:filtered', async () => {
  quizCompleted = true;

  const config = window.klaviyoConfig;
  const email = window.OptimealResults?.klaviyoEmail;
  const petCount = window.OptimealResults?.petCount || 1;

  if (!config?.companyId) console.error('[quiz-klaviyo] Missing Klaviyo companyId (public API key).');
  if (!config?.quizListId) console.error('[quiz-klaviyo] Missing Klaviyo quizListId (quiz completion list).');
  if (!email) console.warn('[quiz-klaviyo] No email provided, skipping subscription.');

  if (config?.companyId && config?.quizListId && email) {
    const properties = {
      ...buildKlaviyoProperties(petCount),
      ...buildRecommendedProductsProperties(),
      quiz_results_url: buildResultsUrl(),
    };
    console.log('[quiz-klaviyo] Subscribing to quiz list:', { email, properties, quizListId: config.quizListId });
    try {
      const res = await subscribeToList(email, config.quizListId, properties);
      const responseText = await res.text();
      if (!res.ok) {
        console.error('[quiz-klaviyo] Subscription error:', res.status, res.statusText, responseText);
      } else {
        console.log('[quiz-klaviyo] Subscribed successfully.');
      }
    } catch (err) {
      console.error('[quiz-klaviyo] Failed to subscribe:', err);
    }
  }

  window.dispatchEvent(new Event('quiz:close'));
});

// ─── Flow 2: Quiz abandoned ────────────────────────────────────────────────────

let abandonedFired = false;

/**
 * Whether the user actually engaged with the quiz, as opposed to merely having a
 * state object — quiz-renderer.js seeds window.OptimealQuizState at module-eval time
 * on every page of the site (quiz-modal.liquid is rendered from theme.liquid), so
 * "state exists" is true even on pages where the quiz was never opened.
 *
 * Mirrors the hasProgress test in quiz-renderer.js saveProgress(): the quiz only
 * considers itself worth persisting under exactly these conditions.
 */
function hasQuizEngagement() {
  const state = window.OptimealQuizState;
  if (!state) return false;
  return (
    state._familyQuantityAnswerIndex != null ||
    state.currentStep > 1 ||
    (state.pets || []).some(
      (pet) =>
        Object.keys(pet.answers || {}).length > 0 ||
        Object.keys(pet.formAnswers || {}).length > 0,
    )
  );
}

async function trackAbandoned({ keepalive = false } = {}) {
  if (quizCompleted || abandonedFired) return;
  if (!hasQuizEngagement()) return;
  abandonedFired = true;

  // GA4 + Meta abandon fires here so every path — close (×) button, tab-close and
  // navigate-away (pagehide) — is covered exactly once (guarded above), and
  // independent of Klaviyo subscriber status (the email checks below are Klaviyo's
  // concern only). keepalive → sendBeacon transport so it survives page unload.
  const state = window.OptimealQuizState || {};
  window.QuizAnalytics?.abandon({
    lastStep: state.currentStep || 1,
    totalSteps: (window.quiz && window.quiz.totalSteps) || 0,
    beacon: keepalive,
  });

  const config = window.klaviyoConfig;
  const email = getKlaviyoSubscriberEmail();

  if (!email) {
    console.log('[quiz-klaviyo] Abandoned: no existing subscriber email found in __kla_id cookie, skipping.');
    return;
  }
  if (!config?.companyId || !config?.abandonedListId) {
    console.warn('[quiz-klaviyo] Abandoned: missing companyId or abandonedListId, skipping.');
    return;
  }

  const properties = buildAbandonedProperties();
  console.log('[quiz-klaviyo] Tracking quiz abandoned:', { email, properties });

  try {
    const res = await subscribeToList(email, config.abandonedListId, properties, { keepalive });
    if (keepalive) {
      // Can't read response body on pagehide — just log that we sent it
      console.log('[quiz-klaviyo] Abandoned beacon sent (keepalive).');
    } else {
      const responseText = await res.text();
      if (!res.ok) {
        console.error('[quiz-klaviyo] Abandoned error:', res.status, res.statusText, responseText);
      } else {
        console.log('[quiz-klaviyo] Abandoned tracked successfully.');
      }
    }
  } catch (err) {
    console.error('[quiz-klaviyo] Failed to track abandoned:', err);
  }
}

// Trigger: user clicks the quiz close (×) button. trackAbandoned() fires the GA4 +
// Meta abandon (plus the per-cat quiz_pet_summary events) and the Klaviyo subscribe,
// both guarded against double-fire and against the no-engagement case (opening the
// quiz and closing it again isn't an abandon — quiz_open already covers that).
document.querySelector('.quiz__close')?.addEventListener('click', () => {
  trackAbandoned();
});

// Trigger: user navigates away or closes the tab mid-quiz.
window.addEventListener('pagehide', () => {
  trackAbandoned({ keepalive: true });
});

// ─── Flow 3: Meal plan selection (results page) ────────────────────────────────
//
// Fired by quiz-results.js (Save button in the product edit modal) — this is a
// separate page load from the quiz, so there's no email in memory. Identifies
// the visitor the same way Flow 2 does: the __kla_id cookie set by the site's
// embedded Klaviyo signup popup. Refreshes the SAME profile/list from quiz
// completion, since that one only ever carried the initial algorithmic
// recommendation, not what the customer actually chose.
document.addEventListener('optimeal:meal-plan-selected', async (e) => {
  const config = window.klaviyoConfig;
  const email = getKlaviyoSubscriberEmail();

  if (!email) {
    console.log('[quiz-klaviyo] Meal plan selected: no existing subscriber email found in __kla_id cookie, skipping.');
    return;
  }
  if (!config?.companyId || !config?.quizListId) {
    console.warn('[quiz-klaviyo] Meal plan selected: missing companyId or quizListId, skipping.');
    return;
  }

  const { petIndex, items } = e.detail;
  const prefix = petIndex == null ? 'trial_' : `pet_${petIndex + 1}_`;
  const properties = {
    [`${prefix}selected_products`]: items.map((item) => item.item_name).join(', '),
    [`${prefix}selected_pouches`]: items.reduce((sum, item) => sum + item.quantity * 12, 0),
  };

  console.log('[quiz-klaviyo] Updating meal plan selection:', { email, properties });
  try {
    const res = await subscribeToList(email, config.quizListId, properties);
    const responseText = await res.text();
    if (!res.ok) {
      console.error('[quiz-klaviyo] Meal plan update error:', res.status, res.statusText, responseText);
    } else {
      console.log('[quiz-klaviyo] Meal plan selection synced.');
    }
  } catch (err) {
    console.error('[quiz-klaviyo] Failed to sync meal plan selection:', err);
  }
});

// ─── Test helper ──────────────────────────────────────────────────────────────
//
// Navigate to any page with the quiz loaded (e.g. homepage), open the console,
// and run one of:
//
//   testKlaviyoSignup('dan.perlman.prog@gmail.com')
//   testKlaviyoSignup('danny@onemoreagency.io', '2-cats')
//   testKlaviyoSignup('accounts@onemoreagency.io', '3-cats')
//
//   testKlaviyoAbandoned('dan.perlman.prog@gmail.com')
//
// Scenarios: '1-cat' (default), '2-cats', '3-cats'
// ─────────────────────────────────────────────────────────────────────────────

const TEST_SCENARIOS = {
  '1-cat': {
    pet_count: 1,
    pet_1_name: 'Whiskers',
    pet_1_activity: 'Low',
    pet_1_weight_lb: 10,
    pet_1_pouches: 48,
    pet_1_recommendations: [
      { title: 'Chicken & Tuna Pâté', url: '/products/chicken-tuna-pate', price: '39.99 GBP', image_url: '' },
      { title: 'Salmon Mousse', url: '/products/salmon-mousse', price: '39.99 GBP', image_url: '' },
      { title: 'Duck & Rabbit Terrine', url: '/products/duck-rabbit-terrine', price: '39.99 GBP', image_url: '' },
    ],
  },
  '2-cats': {
    pet_count: 2,
    pet_1_name: 'Whiskers',
    pet_1_activity: 'Low',
    pet_1_weight_lb: 10,
    pet_1_pouches: 48,
    pet_1_recommendations: [
      { title: 'Chicken & Tuna Pâté', url: '/products/chicken-tuna-pate', price: '39.99 GBP', image_url: '' },
      { title: 'Salmon Mousse', url: '/products/salmon-mousse', price: '39.99 GBP', image_url: '' },
    ],
    pet_2_name: 'Luna',
    pet_2_activity: 'Normal',
    pet_2_weight_lb: 12,
    pet_2_pouches: 60,
    pet_2_recommendations: [
      { title: 'Turkey & Pumpkin Pâté', url: '/products/turkey-pumpkin-pate', price: '39.99 GBP', image_url: '' },
      { title: 'Salmon Mousse', url: '/products/salmon-mousse', price: '39.99 GBP', image_url: '' },
    ],
  },
  '3-cats': {
    pet_count: 3,
    pet_1_name: 'Whiskers',
    pet_1_activity: 'Normal',
    pet_1_weight_lb: 9,
    pet_1_pouches: 36,
    pet_1_recommendations: [
      { title: 'Chicken & Tuna Pâté', url: '/products/chicken-tuna-pate', price: '39.99 GBP', image_url: '' },
    ],
    pet_2_name: 'Luna',
    pet_2_activity: 'Low',
    pet_2_weight_lb: 12,
    pet_2_pouches: 48,
    pet_2_recommendations: [
      { title: 'Turkey & Pumpkin Pâté', url: '/products/turkey-pumpkin-pate', price: '39.99 GBP', image_url: '' },
      { title: 'Salmon Mousse', url: '/products/salmon-mousse', price: '39.99 GBP', image_url: '' },
    ],
    pet_3_name: 'Mochi',
    pet_3_activity: 'High',
    pet_3_weight_lb: 8,
    pet_3_pouches: 36,
    pet_3_recommendations: [
      { title: 'Duck & Rabbit Terrine', url: '/products/duck-rabbit-terrine', price: '39.99 GBP', image_url: '' },
      { title: 'Chicken & Tuna Pâté', url: '/products/chicken-tuna-pate', price: '39.99 GBP', image_url: '' },
    ],
  },
};

window.testKlaviyoSignup = async function (email, scenario = '1-cat') {
  const config = {
    companyId: window.klaviyoConfig?.companyId || 'SZMuPz',
    quizListId: window.klaviyoConfig?.quizListId || 'XZ6GU5',
  };

  const properties = TEST_SCENARIOS[scenario];
  if (!properties) {
    console.error(`[quiz-klaviyo-test] Unknown scenario "${scenario}". Options: ${Object.keys(TEST_SCENARIOS).join(', ')}`);
    return;
  }

  properties.quiz_results_url = `${window.location.origin}/pages/quiz-results`;

  console.log(`[quiz-klaviyo-test] Sending test signup → ${email} (scenario: ${scenario}, list: ${config.quizListId})`, properties);

  try {
    const res = await fetch(`https://a.klaviyo.com/client/subscriptions/?company_id=${config.companyId}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'revision': '2024-10-15' },
      body: JSON.stringify({
        data: {
          type: 'subscription',
          attributes: {
            profile: {
              data: { type: 'profile', attributes: { email, properties } },
            },
          },
          relationships: {
            list: { data: { type: 'list', id: config.quizListId } },
          },
        },
      }),
    });

    const responseText = await res.text();
    if (!res.ok) {
      console.error('[quiz-klaviyo-test] Error:', res.status, res.statusText, responseText);
    } else {
      console.log(`[quiz-klaviyo-test] ✓ Subscribed ${email} with scenario "${scenario}"`);
    }
  } catch (err) {
    console.error('[quiz-klaviyo-test] Failed:', err);
  }
};

window.testKlaviyoAbandoned = async function (email) {
  const config = {
    companyId: window.klaviyoConfig?.companyId || 'SZMuPz',
    abandonedListId: window.klaviyoConfig?.abandonedListId || '',
  };

  if (!config.abandonedListId) {
    console.error('[quiz-klaviyo-test] abandonedListId not set. Add it in Shopify theme settings → Klaviyo → Quiz Abandoned list ID.');
    return;
  }

  const properties = {
    quiz_abandoned: true,
    quiz_last_step: 2,
    quiz_pet_count: 1,
    quiz_pet_1_name: 'Whiskers',
  };

  console.log(`[quiz-klaviyo-test] Sending test abandoned → ${email}`, properties);

  try {
    const res = await fetch(`https://a.klaviyo.com/client/subscriptions/?company_id=${config.companyId}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'revision': '2024-10-15' },
      body: JSON.stringify({
        data: {
          type: 'subscription',
          attributes: {
            profile: {
              data: { type: 'profile', attributes: { email, properties } },
            },
          },
          relationships: {
            list: { data: { type: 'list', id: config.abandonedListId } },
          },
        },
      }),
    });

    const responseText = await res.text();
    if (!res.ok) {
      console.error('[quiz-klaviyo-test] Error:', res.status, res.statusText, responseText);
    } else {
      console.log(`[quiz-klaviyo-test] ✓ Abandoned tracked for ${email}`);
    }
  } catch (err) {
    console.error('[quiz-klaviyo-test] Failed:', err);
  }
};
